import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import { Form, Upload } from 'antd';
import styled from 'styled-components';

import { createPhotos, getCommunityPhotos } from 'apis/CommunityAPI';
import CommunityInfoConfirmationModal from 'components/CommunityInfo/CommunityInfoConfirmationModal';
import { useRootContext } from 'components/layout/RootContext';
import { CommunityType } from 'types/communities';
import { convertToBase64, fetchPhotos } from 'utils/helpers/files';

import PhotoUploadThumbnail from './PhotoUploadThumbnail';
import { FILE_SOURCE, STATUS, SUPPORTED_IMAGE_TYPES } from '../../../../../constants';
import ShopDataModalDraggerField from '../../../../ShopData/documents/ShopDataModalDraggerField';
import { useEditDataModalContext } from '../../../common/edit/EditDataModalContext';
import { useCommunityInfoContext } from '../../../CommunityInfoContext';

const DraggerFieldWrapper = styled.div`
  padding: 20px 15px;

  && .ant-upload-list {
    margin-top: 15px;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 15px;
    align-items: flex-start;

    .ant-upload-list-item-container {
      flex: 0 0 134px;
      width: 134px;
      height: auto;
      margin-top: 0;
    }
  }
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 60vh;
  box-sizing: border-box;
  overflow-y: auto;
  flex-direction: column;
  padding: 10px;
`;

type Props = {
  community?: CommunityType;
  disabled?: boolean;
};

const PhotosEdit = ({ community, disabled = false }: Props) => {
  const { showMessage } = useRootContext();
  const { form, modified, loading, setModified, setLoading, onSuccess } = useEditDataModalContext();
  const { communityInfo } = useCommunityInfoContext();
  const isSubmittingRef = useRef(false);
  const [fetchLoading, setFetchLoading] = useState(STATUS.IDLE);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    data,
    isLoading: photosLoading,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: ['community', 'photos', communityInfo?.id, community?.id],
    queryFn: () => getCommunityPhotos(communityInfo?.id ?? community?.id ?? -1, { source: [FILE_SOURCE.MEDIA] }),
    enabled: Boolean(communityInfo || community),
  });

  useEffect(() => {
    const fetchImages = async () => {
      if (isFetched) {
        const currentMockedImages = await fetchPhotos(data);
        form?.setFieldValue('photos', currentMockedImages);
      }
      setFetchLoading(STATUS.LOADED);
    };
    setFetchLoading(STATUS.LOADING);
    fetchImages();
  }, [data, isFetched]);

  const handleConfirmSubmit = async () => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);

    try {
      let values;
      try {
        values = await form?.validateFields();
      } catch (error) {
        if (error && typeof error === 'object' && 'errorFields' in error) return;
        throw error;
      }

      const photos = values.photos;
      // Prepare Photos payload
      const photosDataPayload = await Promise.all(
        (photos ?? []).map((file: any) => convertToBase64(file.originFileObj))
      );

      const photosPayload = photosDataPayload.map((item, index) => ({
        id: photos?.length ? photos[index].id : undefined,
        name: photos?.length ? photos[index].name || 'Untitled' : 'Untitled',
        mock: photos?.length ? photos[index].mock : undefined,
        category: photos?.length ? photos[index].category : undefined,
        file: {
          name: photos?.length ? photos[index].name || 'Untitled' : 'Untitled',
          data: item,
        },
      }));

      await createPhotos(communityInfo?.id ?? community?.id ?? -1, { images: photosPayload });
      showMessage('success', 'Community Photos updated successfully!');
      form?.setFieldValue('photos', []);
      onSuccess();
      refetch();
    } catch (e) {
      showMessage('error', 'Trouble updating community photos!');
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleValuesChanged = () => {
    if (!modified) {
      setModified(true);
    }
  };

  const handleBeforeUpload = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension || !SUPPORTED_IMAGE_TYPES.EXTENSIONS.includes(extension)) {
      showMessage(
        'error',
        `Unsupported image format: .${extension || 'unknown'}. Please upload a JPG, PNG, GIF or WEBP file.`
      );
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  return (
    <Container>
      {!photosLoading && fetchLoading === STATUS.LOADED && (
        <Form
          form={form}
          onValuesChange={handleValuesChanged}
          onFinish={() => setShowConfirmation(true)}
          disabled={disabled}
        >
          <DraggerFieldWrapper>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const currentImages = getFieldValue('photos');
                return (
                  <ShopDataModalDraggerField
                    name={['photos']}
                    label="photos"
                    dataTestId="cd-community-photos-upload"
                    multiple
                    accept={SUPPORTED_IMAGE_TYPES.MIME_TYPES.join(',')}
                    beforeUpload={handleBeforeUpload}
                    itemRender={(originNode: React.ReactNode, file: any, fileList: any, actions: any) =>
                      PhotoUploadThumbnail(originNode, file, fileList, actions, disabled)
                    }
                    fileList={currentImages}
                    disabled={disabled}
                  />
                );
              }}
            </Form.Item>
          </DraggerFieldWrapper>
        </Form>
      )}
      <CommunityInfoConfirmationModal
        showCommunityIncentivesConfirmationModal={showConfirmation}
        communityName={communityInfo?.comunity_name}
        loading={loading}
        onConfirm={handleConfirmSubmit}
        onClose={() => setShowConfirmation(false)}
      />
    </Container>
  );
};

export default PhotosEdit;
